import IconLeftArrow from '../../Icons/IconLeftArrow/IconLeftArrow';
import IconBin from '../../Icons/IconBin/IconBin';
import IconEditOutlined from '../../Icons/IconEditOutlined/IconEditOutlined';
import { ElementSize } from '../../../types/element-size';
import { IActionsDropdown, IActionsTag } from '../../../types/tableDropdown';
import { IRecipeStepEditable } from '../../../types/recipes-types';
import Table from '../../Table/Table';
import { useMemo, useState } from 'react';
import {
  initialStepValues,
  stepsLimitCount,
  undefinedIndex,
  validationStepSchema,
} from '../../../pages/Recipes/constants';
import Modal from '../../Modal/Modal';
import ModalFullScreen from '../../ModalFullScreen/ModalFullScreen';
import { RecipeStepForm } from '../../../pages/Recipes/Components/RecipeStepForm';
import { generateStepName } from '../../../pages/Recipes/utils';
import InteractiveButton from '../../InteractiveButton/InteractiveButton';
import { RootState } from '../../../store/store';
import { connect } from 'react-redux';
import CycleMeasurement from '../../../enums/cycle-measurement';
import getMeasurementPreference from '../../../utils/getMeasurementPreference';
import Tooltip from '../../Tooltip';
import { useTranslation } from 'react-i18next';

export interface StepsTableProps {
  managedSteps: IRecipeStepEditable[];
  onRemoveStep: (value: number) => any;
  onChangeStep: (idx: number, value: IRecipeStepEditable) => any;
  onPressUpdate: (value: IRecipeStepEditable[]) => any;
  moveUpStep: (idx: number) => any;
  moveDownStep: (idx: number) => any;
  onCreateStep: (value: IRecipeStepEditable) => any;
}

type ComponentProps = ReturnType<typeof mapState> & StepsTableProps;

const ManagedStepsTableView = ({
  managedSteps,
  onRemoveStep,
  onChangeStep,
  moveDownStep,
  moveUpStep,
  onPressUpdate,
  onCreateStep,
  units,
}: ComponentProps) => {
  const [deleteRecipeIndex, setDeleteRecipeIndex] = useState<number>(undefinedIndex);
  const [changeRecipeIndex, setChangeRecipeIndex] = useState<number>(undefinedIndex);
  const [open, setOpen] = useState(false);

  const { t } = useTranslation();

  const viewValues =
    managedSteps.map((item, idx) => ({
      Name: `${idx + 1}. ${generateStepName(item)}`,
      id: `${idx}`,
      idx,
      order: item.order,
    })) || [];

  const isDisabledBtn = useMemo(() => {
    return managedSteps.length >= stepsLimitCount;
  }, [managedSteps]);

  const editStepTitle = useMemo(() => {
    if (changeRecipeIndex < 0) {
      return '';
    }
    return `Edit Recipe Step #${managedSteps[changeRecipeIndex].order + 1}`;
  }, [changeRecipeIndex]);

  const closeDeleteModal = () => {
    setDeleteRecipeIndex(undefinedIndex);
  };
  const closeChangeModal = () => {
    setChangeRecipeIndex(undefinedIndex);
  };
  const onSubmitDelete = () => {
    onRemoveStep(deleteRecipeIndex);
    closeDeleteModal();
  };

  const withContractUnits = (values: IRecipeStepEditable): IRecipeStepEditable => ({
    ...values,
    isCelsius: units === CycleMeasurement.Celsius,
    units,
  });

  const onSubmitChange = async (values: IRecipeStepEditable) => {
    onChangeStep(changeRecipeIndex, withContractUnits(values));
    closeChangeModal();
  };

  const onSubmitHandler = async (values: IRecipeStepEditable) => {
    await onCreateStep(withContractUnits(values));
    setOpen(false);
  };

  const actionsButtons: IActionsDropdown[] = [
    {
      icon: () => (
        <Tooltip hint={t('commonText.edit')}>
          <IconEditOutlined stroke="inherit" iconClassName="text-inherit" size={ElementSize.XXS} />
        </Tooltip>
      ),
      onClick: (_id, payload: (typeof viewValues)[number]) => setChangeRecipeIndex(payload.idx),
      loading: false,
      tag: IActionsTag.Link,
    },
    {
      icon: () => (
        <Tooltip hint={t('commonText.delete')}>
          <IconBin fill="inherit" iconClassName="text-inherit dark:text-white" size={ElementSize.XXS} />
        </Tooltip>
      ),
      onClick: (_id, payload: (typeof viewValues)[number]) => setDeleteRecipeIndex(payload.idx),
    },
    {
      icon: () => (
        <Tooltip hint={t('report.steps.moveStepEarlier')} hintClassName="right-[-220%] bottom-7" isCustomSettings>
          <div style={{ transform: 'rotate(90deg)' }}>
            <IconLeftArrow fill="inherit" iconClassName="text-inherit dark:text-white" size={ElementSize.XXS} />
          </div>
        </Tooltip>
      ),
      onClick: (_id, payload: (typeof viewValues)[number]) => moveUpStep(payload.idx),
    },

    {
      icon: () => (
        <Tooltip hint={t('report.steps.moveStepLater')} hintClassName="right-[-40%] bottom-7" isCustomSettings>
          <div style={{ transform: 'rotate(-90deg)' }}>
            <IconLeftArrow fill="inherit" iconClassName="text-inherit dark:text-white" size={ElementSize.XXS} />
          </div>
        </Tooltip>
      ),
      onClick: (_id, payload: (typeof viewValues)[number]) => moveDownStep(payload.idx),
    },
  ];

  const onPressUpdateHandler = () => {
    const steps = managedSteps.map((step) => {
      return {
        ...step,
        isCelsius: step?.isCelsius ?? step?.units === CycleMeasurement.Celsius,
      };
    });
    onPressUpdate(steps);
  };

  return (
    <>
      <div className="flex w-full  flex-col gap-5">
        <div className="flex h-10 w-full flex-row items-center justify-between">
          <h2 className="text-title-md2 font-semibold text-black dark:text-white">Managed Steps</h2>
        </div>
        <div>
          <Table values={viewValues} defaultKeys={['Name']} actionsButtons={actionsButtons} />
        </div>
        <div className="flex w-full justify-between">
          <InteractiveButton text="Create Step" disabled={isDisabledBtn} onClick={() => setOpen(true)} />
          <InteractiveButton text="Update Steps" onClick={onPressUpdateHandler} />
        </div>
      </div>
      <Modal
        modalTitle="Recipe Step Deletion"
        isVisible={deleteRecipeIndex >= 0}
        content={`Are you sure you want to delete the '${
          deleteRecipeIndex >= 0 && generateStepName(managedSteps[deleteRecipeIndex])
        }' step?`}
        acceptBtnText="Delete"
        onClose={closeDeleteModal}
        onAccept={onSubmitDelete}
        showAcceptBtn
        showCancelBtn
      />
      <ModalFullScreen
        isVisible={changeRecipeIndex >= 0}
        modalTitle={editStepTitle}
        onClose={closeChangeModal}
        className="sm:max-h-fit sm:max-w-100 sm:rounded-lg lg:max-w-142.5 lg:px-17.5 lg:py-15"
      >
        {changeRecipeIndex >= 0 && (
          <RecipeStepForm
            initialValues={managedSteps[changeRecipeIndex]}
            validationSchema={validationStepSchema}
            onSubmit={onSubmitChange}
            submitButtonName="Update"
            prefferedUnits={units}
            disableUnitsSwitcher
          />
        )}
      </ModalFullScreen>

      <ModalFullScreen
        isVisible={open}
        modalTitle="Create Step"
        onClose={() => setOpen(false)}
        className="sm:max-h-fit sm:max-w-100 sm:rounded-lg lg:max-w-142.5 lg:px-17.5 lg:py-15"
      >
        <RecipeStepForm
          initialValues={initialStepValues}
          validationSchema={validationStepSchema}
          onSubmit={onSubmitHandler}
          submitButtonName="Create"
          prefferedUnits={units}
          disableUnitsSwitcher
        />
      </ModalFullScreen>
    </>
  );
};

const mapState = (state: RootState) => ({
  units: getMeasurementPreference(state.contracts.contract, state.locations.currentLocation),
});

export const ManagedStepsTable = connect(mapState, null)(ManagedStepsTableView);
